import React, { useState, useEffect, useRef } from "react";
import { Snackbar, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Header from "@shared/header";
import Loader from "@shared/loader";
import {
  setOTPInValid,
  setSuccess,
  verifyOtp,
} from "../../../store/slices/verifyOTPslice";
import { DigitsContainer, OtpBody, OtpInput } from "./verifyOTP.styles";
import { requestOtp } from "store/slices/requestOTPSlice";
import { ROUTES_VARIANT } from "constants/routesVariant";
import { KYCConstants } from "constants/application";

function VerifyOTP() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const info = useSelector((state: any) => state.info.allData);
  const verification = useSelector((state: any) => state.verifyOtp);
  const [otpRequestCounter, setOtpRequestCounter] = useState<number>(0);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [attemptsLeft, setAttemptsLeft] = useState<number>(3);
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [timeLeft, setTimeLeft] = useState(
    Number(process.env.REACT_APP_OTP_DELAY_TIME)
  );
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]); // Function to handle changes in each input box

  useEffect(() => {
    if (verification.success && !verification.OTPInValid) {
      navigate(`/${ROUTES_VARIANT.PROCESS}`, { replace: true });
    }
  }, [navigate, verification.success, verification.OTPInValid]);

  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [timeLeft]);

  const handleChange = (value: string, index: number) => {
    if (isNaN(Number(value))) return; // Prevent non-numeric input

    const newOtp = [...otp];
    // allow only one input
    newOtp[index] = value.substring(value.length - 1);
    setOtp(newOtp);

    // If all inputs are filled, automatically submit the OTP
    const combinedOtp = newOtp.join("");
    if (combinedOtp.length === 4) handleSubmit(combinedOtp);

    if (value && index < 4 - 1 && inputRefs.current[index + 1]) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  // Function to handle the backspace key
  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (
      event.key === "Backspace" &&
      !otp[index] &&
      index > 0 &&
      inputRefs.current[index - 1]
    ) {
      inputRefs.current[index - 1]?.focus();
    }
  }; // Function to handle OTP submission

  const handleSubmit = (otpCode: string) => {
    dispatch(verifyOtp({ otp: otpCode }) as any);
    setAttemptsLeft((prev) => prev - 1);
  };

  const handleOtpRead = (smsOtp: string) => {
    // Extract the first 4 numeric characters
    const extractedOtp = smsOtp.replace(/\D/g, "").slice(0, 4);
    // Ensure the OTP is always an array of 4 characters
    const otpArray = extractedOtp
      .split("")
      .concat(Array(4).fill(""))
      .slice(0, 4);
    setOtp(otpArray);
    if (extractedOtp.length === 4) {
      handleSubmit(extractedOtp);
    }
  };

  // Effect for auto-filling the OTP using WebOTP API
  useEffect(() => {
    // only if user comes through OTP login, allow KYC steps, else navigate user to home page
    if (info?.customerId) {
      if ("OTPCredential" in window) {
        const abortController = new AbortController();

        window.navigator.credentials
          .get({
            // Type assertion for the `otp` property
            otp: { transport: ["sms"] },
            signal: abortController.signal,
          } as any) // Use `any` to bypass TypeScript checks
          .then((otpCredential: any) => {
            if (otpCredential && otpCredential.code) {
              handleOtpRead(otpCredential.code);
            }
          })
          .catch((error) => {
            console.error("Error reading OTP:", error);
          });
      }
    } else {
      navigate("/", { replace: true });
    }
  }, []);

  const handleRetry = () => {
    setOtpRequestCounter((prev) => {
      if (prev + 1 <= Number(process.env.REACT_APP_RESEND_OTP_LIMIT)) {
        setTimeLeft(Number(process.env.REACT_APP_OTP_DELAY_TIME));
        dispatch(requestOtp(info) as any);
        setSnackbarMessage(
          t("otp_sent_attempts_left", {
            optRequestCount:
              Number(process.env.REACT_APP_RESEND_OTP_LIMIT) - prev - 1,
            defaultValue: `OTP sent! ${
              Number(process.env.REACT_APP_RESEND_OTP_LIMIT) - prev - 1
            } resend attempts left`,
          })
        );
      }
      return prev + 1;
    });
    setOtp(["", "", "", ""]);
    //dispatch sending OTP again
    dispatch(setSuccess({ value: false }) as any);
    dispatch(setOTPInValid({ value: false }) as any);
    setAttemptsLeft(3);
  };

  const handleClick = (index: number) => {
    inputRefs.current[index]?.setSelectionRange(1, 1);

    // optional
    if (index > 0 && !otp[index - 1]) {
      inputRefs.current[otp.indexOf("")]?.focus();
    }
  };
  return (
    <>
      <Header />
      <OtpBody>
        <Typography variant="h2">
          {t("enter_otp", {
            phoneNumber: info.mobileNumber,
            defaultValue: `Please enter 4 digit OTP sent via SMS on ${info.mobileNumber}`,
          })}
        </Typography>
        <DigitsContainer>
          {otp.map((digit, index) => (
            <OtpInput
              aria-label={`OTP digit ${index + 1}`}
              autoComplete="off"
              disabled={attemptsLeft <= 0}
              autoFocus={index === 0}
              inputMode="numeric"
              pattern="[0-9]*"
              type="text"
              key={index}
              name={`otp-${index}`}
              value={digit}
              onClick={() => handleClick(index)}
              onChange={(e) => handleChange(e.target.value, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              ref={(el) => {
                inputRefs.current[index] = el;
              }}
            />
          ))}
        </DigitsContainer>
        {otpRequestCounter <=
          Number(process.env.REACT_APP_RESEND_OTP_LIMIT) && (
          <Typography variant="body1" color="#686868">
            {t("otp_sent", { defaultValue: "An OTP has been sent." })}
            {timeLeft > 0 ? (
              <Typography component={"span"} variant="subtitle1" color={"#333"}>
                {" "}
                {t("retry_otp", { defaultValue: "Retry in" })}{" "}
                {Math.floor(timeLeft / 60)}:{("0" + (timeLeft % 60)).slice(-2)}
              </Typography>
            ) : (
              <Typography
                component={"span"}
                variant="subtitle1"
                color="primary"
                onClick={handleRetry}
              >
                {" "}
                {t("resend_otp", { defaultValue: "Resend OTP" })}
              </Typography>
            )}
          </Typography>
        )}
        {verification.OTPInValid && (
          <Typography color="error">
            {t("invalid_otp", {
              defaultValue: "Invalid OTP, Please enter the correct OTP.",
            })}
            <br />
            {t("attempts_left", {
              attemptsLeft: attemptsLeft,
            })}
          </Typography>
        )}{" "}
        {otpRequestCounter >=
          Number(process.env.REACT_APP_RESEND_OTP_LIMIT) + 1 && (
          <>
            <Typography color="error">
              {t("otp_limit", {
                defaultValue: "You've reached the OTP resend limit.",
              })}
            </Typography>
            <Typography variant="body1" color="#686868">
              {t("try_gain_otp", { defaultValue: "Try again in" })}{" "}
              <Typography component={"span"} variant="subtitle1">
                {t("10_mins", { defaultValue: "10 minutes." })}
              </Typography>
            </Typography>
          </>
        )}
      </OtpBody>
      {verification.loading && <Loader />}
      <Snackbar
        open={Boolean(snackbarMessage)}
        onClose={() => setSnackbarMessage("")}
        autoHideDuration={KYCConstants.autoHideDuration}
        message={snackbarMessage}
      />
    </>
  );
}

export default VerifyOTP;
